/**
 * Eingehende E-Rechnung einlesen.
 *
 * Die Datei wird zuerst nur angezeigt - erst nach Sichtkontrolle entsteht
 * daraus eine Eingangsrechnung im Entwurf.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { formatAmount, formatDate, formatPrice, formatQuantity } from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import type { ListResponse, Partner } from '../api/types';
import { PageHeader } from '../components/Layout';
import { SearchSelect } from '../components/SearchSelect';
import { useToast } from '../components/toast';
import { Alert, Field } from '../components/ui';

type PreviewLine = {
  description: string;
  itemNumber: string | null;
  itemId: string | null;
  itemName: string | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  netAmount: number;
  taxRate: number;
};

type Preview = {
  invoice: {
    format: 'cii' | 'ubl';
    invoiceNumber: string;
    documentDate: string;
    serviceDate: string | null;
    dueDate: string | null;
    currency: string;
    supplier: {
      name: string;
      vatId: string | null;
      street: string | null;
      postalCode: string | null;
      city: string | null;
      iban: string | null;
    };
    buyerName: string | null;
    netTotal: number;
    taxTotal: number;
    grossTotal: number;
    warnings: string[];
  };
  supplier: { id: string; number: string; name: string; matchedBy: string } | null;
  lines: PreviewLine[];
  duplicate: { id: string; number: string | null } | null;
};

export function EInvoiceImportPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [xml, setXml] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [chosenPartner, setChosenPartner] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: suppliers } = useQuery({
    queryKey: ['partners', 'supplier', 'import'],
    queryFn: () =>
      api.get<ListResponse<Partner>>(withQuery('/api/partners', { role: 'supplier', limit: 500 })),
  });

  const analyze = useMutation({
    mutationFn: (content: string) =>
      api.post<Preview>('/api/einvoice/preview', { xml: content, fileName }),
    onSuccess: (result) => {
      setPreview(result);
      setChosenPartner(result.supplier?.id ?? '');
      setError(null);
    },
    onError: (caught) => {
      setPreview(null);
      setError(caught instanceof ApiError ? caught.message : 'Die Datei konnte nicht gelesen werden.');
    },
  });

  const importInvoice = useMutation({
    mutationFn: () =>
      api.post<{ document: { id: string }; deviation: number; message: string | null }>(
        '/api/einvoice/import',
        { xml, fileName, partnerId: chosenPartner || undefined },
      ),
    onSuccess: (result) => {
      if (result.message) toast.error(result.message);
      else toast.success('Die Eingangsrechnung wurde als Entwurf angelegt.');
      navigate(`/einkauf/eingangsrechnungen/${result.document.id}`);
    },
    onError: (caught) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Der Import ist fehlgeschlagen.'),
  });

  async function handleFile(file: File) {
    const content = await file.text();
    setXml(content);
    setFileName(file.name);
    analyze.mutate(content);
  }

  return (
    <>
      <PageHeader
        title="E-Rechnung einlesen"
        subtitle="XRechnung (UBL) und ZUGFeRD-XML (CII) nach EN 16931"
      />

      <div className="card">
        <div className="card-body">
          <Alert kind="info">
            Seit dem 1. Januar 2025 muss jedes deutsche Unternehmen E-Rechnungen empfangen und
            verarbeiten können. Aus der Datei entsteht hier eine Eingangsrechnung im Entwurf — die
            sachliche Prüfung und das Festschreiben bleiben bei Ihnen. Das Original wird
            unverändert archiviert.
          </Alert>

          <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files[0];
              if (file) void handleFile(file);
            }}
            style={{
              padding: '28px 20px',
              border: '2px dashed var(--border-strong)',
              borderRadius: 'var(--radius)',
              textAlign: 'center',
              background: 'var(--bg-sunken)',
            }}
          >
            <p style={{ margin: '0 0 10px' }}>XML-Datei hierher ziehen oder auswählen</p>
            <input
              type="file"
              accept=".xml,text/xml,application/xml"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            {fileName ? (
              <div className="faint" style={{ marginTop: 10 }}>
                Gelesen: {fileName}
              </div>
            ) : null}
          </div>

          {analyze.isPending ? (
            <p className="muted" style={{ marginTop: 12 }}>
              Die Datei wird gelesen …
            </p>
          ) : null}

          {error ? (
            <div style={{ marginTop: 16 }}>
              <Alert kind="error">{error}</Alert>
            </div>
          ) : null}
        </div>
      </div>

      {preview ? (
        <>
          {preview.invoice.warnings.map((warning) => (
            <Alert kind="warning" key={warning}>
              {warning}
            </Alert>
          ))}

          {preview.duplicate ? (
            <Alert kind="warning">
              Eine Rechnung mit dieser Nummer wurde bereits erfasst. Bitte prüfen Sie, ob es sich um
              eine Dublette handelt.
            </Alert>
          ) : null}

          <div className="card">
            <div className="card-header">
              <h2>Rechnungsdaten aus der Datei</h2>
              <span className="faint">
                Format: {preview.invoice.format === 'cii' ? 'CII (ZUGFeRD)' : 'UBL (XRechnung)'}
              </span>
            </div>
            <div className="card-body">
              <dl className="definition-list">
                <dt>Rechnungsnummer</dt>
                <dd className="mono">{preview.invoice.invoiceNumber}</dd>
                <dt>Rechnungsdatum</dt>
                <dd>{formatDate(preview.invoice.documentDate)}</dd>
                <dt>Leistungsdatum</dt>
                <dd>{preview.invoice.serviceDate ? formatDate(preview.invoice.serviceDate) : '—'}</dd>
                <dt>Fällig am</dt>
                <dd>{preview.invoice.dueDate ? formatDate(preview.invoice.dueDate) : '—'}</dd>
                <dt>Aussteller</dt>
                <dd>
                  {preview.invoice.supplier.name}
                  {preview.invoice.supplier.vatId ? ` · ${preview.invoice.supplier.vatId}` : ''}
                  <div className="faint">
                    {preview.invoice.supplier.street}
                    {preview.invoice.supplier.city
                      ? `, ${preview.invoice.supplier.postalCode ?? ''} ${preview.invoice.supplier.city}`
                      : ''}
                  </div>
                </dd>
                <dt>Empfänger laut Datei</dt>
                <dd>{preview.invoice.buyerName ?? '—'}</dd>
                <dt>Beträge</dt>
                <dd className="mono">
                  {formatAmount(preview.invoice.netTotal)} netto ·{' '}
                  {formatAmount(preview.invoice.taxTotal)} USt ·{' '}
                  <strong>{formatAmount(preview.invoice.grossTotal)} brutto</strong>
                </dd>
              </dl>

              <div style={{ marginTop: 18, maxWidth: 480 }}>
                <Field
                  label="Lieferant im System"
                  required
                  hint={
                    preview.supplier
                      ? `Automatisch erkannt über ${preview.supplier.matchedBy}`
                      : 'Kein passender Lieferant gefunden — bitte auswählen oder zuerst anlegen'
                  }
                >
                  <SearchSelect
                    value={chosenPartner}
                    onChange={setChosenPartner}
                    placeholder="Name oder Nummer tippen …"
                    options={(suppliers?.items ?? []).map((partner) => ({
                      value: partner.id,
                      label: `${partner.number} · ${partner.name}`,
                    }))}
                  />
                </Field>
              </div>
            </div>

            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Bezeichnung</th>
                    <th className="shrink">Artikel im System</th>
                    <th className="numeric">Menge</th>
                    <th className="numeric">Einzelpreis</th>
                    <th className="numeric">USt</th>
                    <th className="numeric">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((line, index) => (
                    <tr key={index}>
                      <td>
                        {line.description}
                        {line.itemNumber ? (
                          <div className="faint mono">{line.itemNumber}</div>
                        ) : null}
                      </td>
                      <td>
                        {line.itemId ? (
                          <span className="muted">{line.itemName}</span>
                        ) : (
                          <span className="faint">nicht zugeordnet</span>
                        )}
                      </td>
                      <td className="numeric">{formatQuantity(line.quantity)}</td>
                      <td className="numeric">{formatPrice(line.unitPrice)}</td>
                      <td className="numeric muted">{line.taxRate / 100} %</td>
                      <td className="numeric">{formatAmount(line.netAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card-footer">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!chosenPartner || importInvoice.isPending}
                onClick={() => importInvoice.mutate()}
              >
                {importInvoice.isPending ? 'Wird übernommen …' : 'Als Eingangsrechnung übernehmen'}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
